import React from "react"
import { Addition } from "../models/addition"
import { AdditionGroup } from "../models/additionGroup"
import { CartProduct } from "../models/cartProduct"
import { CollectionMethod } from "../models/collection-method"
import { Order } from "../models/order"
import { Product } from "../models/product"
import { BaseRecommendedProduct } from "../models/recommended-product"
import { CollectionMethodIdentifiers } from "./collection-method-identifiers"

export const currencyDecimals = new Map([["ISK", 0]])

export const getCurrencyDecimals = (currency: string) => {
  return currencyDecimals.get(currency) ?? 2
}

export const divideByHundred = (price: number) => {
  const roundedPrice = parseFloat((price / 100).toFixed(2))
  return roundedPrice
}

export const formatMoneyAmount = (
  amount: number,
  currency: string,
  language: string
) => {
  const decimals = getCurrencyDecimals(currency)
  const finalAmount = amount / Math.pow(10, decimals)
  const locale = new Intl.Locale(language)
  const localeString = locale.baseName
  return new Intl.NumberFormat(localeString, {
    style: "currency",
    currency: currency,
  }).format(finalAmount)
}

// Sorting additions descending by base price as primary sort
// and additional selection price as secondary sort
// We loop through the additions and subtract limit from each addition until limit is used
export const getAdditionGroupWithLimitSubtracted = (
  a: AdditionGroup,
  collectionMethod: CollectionMethod
) => {
  let limit = a.shared_option_selection ? a.shared_option_selection.limit : 0
  return {
    ...a,
    additions: [...a.additions]
      .sort((a, b) => {
        const basePriceA = calculateBaseProductPrice(a, collectionMethod)
        const basePriceB = calculateBaseProductPrice(b, collectionMethod)
        if (basePriceA === basePriceB) {
          return (
            b.additional_selection_price.amount -
            a.additional_selection_price.amount
          )
        }
        return basePriceB - basePriceA
      })
      .map((ad) => {
        if (limit > 0 && ad.count > 0) {
          const toSubtract = ad.count > limit ? limit : ad.count
          limit -= toSubtract
          return {
            ...ad,
            count: ad.count - toSubtract,
          }
        } else {
          return ad
        }
      }),
  }
}

// We use the above function to get the addition groups with the limit subtracted and sorted from price
export const getAdditionGroupsWithSortedAdditionsAndSharedOptionLimitSubtracted =
  (additionGroups: AdditionGroup[], collectionMethod: CollectionMethod) => {
    return additionGroups.map((a: AdditionGroup) => {
      return getAdditionGroupWithLimitSubtracted(a, collectionMethod)
    })
  }

export const calculateAdditionGroupsTotalPrice = (
  collectionMethod: CollectionMethod,
  additionGroups: AdditionGroup[],
  startPrice: number,
  quantity: number
) => {
  const sortedAdditionGroups =
    getAdditionGroupsWithSortedAdditionsAndSharedOptionLimitSubtracted(
      additionGroups,
      collectionMethod
    )

  // We know that the shared price is used for the first x additions
  // where x is the limit of the shared option selection
  // So if we know the original count and the current count
  // we can calculate how many additions have used the shared price as counts are adjusted in the above loop
  const price = sortedAdditionGroups.reduce((acc, next: AdditionGroup) => {
    const originalAdditionGroupCount =
      additionGroups
        .find((a) => a.id === next.id)
        ?.additions.reduce((acc, next) => {
          return acc + next.count
        }, 0) || 0
    const currentAdditionGroupCount =
      next.additions.reduce((acc, next) => {
        return acc + next.count
      }, 0) || 0
    const countThatUsesSharedPrice =
      originalAdditionGroupCount - currentAdditionGroupCount
    next.additions.forEach((a: Addition) => {
      if (a.count > 0) {
        const additionPrice = calculateBaseProductPrice(a, collectionMethod)
        // First addition has additionPrice, rest has add-on price
        const basePrice = additionPrice * quantity
        const addOnQuantity = a.count - 1
        const addOnPrice =
          addOnQuantity * a.additional_selection_price.amount * quantity
        acc += basePrice + addOnPrice
      }
    })

    return (
      acc +
      countThatUsesSharedPrice *
        (next.shared_option_selection?.price.amount || 0) *
        quantity
    )
  }, startPrice)

  return price
}

export const calculateTotalProductPrice = (
  product: Product | Addition | BaseRecommendedProduct,
  collectionMethod: CollectionMethod,
  additionGroups?: AdditionGroup[] | undefined,
  quantity: number = 1
) => {
  const productPrice = calculateBaseProductPrice(product, collectionMethod)
  if (!additionGroups) {
    return productPrice * quantity
  }

  return calculateAdditionGroupsTotalPrice(
    collectionMethod,
    additionGroups,
    productPrice * quantity,
    quantity
  )
}

export const calculateBaseProductPrice = (
  product: Product | Addition | BaseRecommendedProduct,
  collectionMethod: CollectionMethod
) => {
  const productPrice =
    collectionMethod === CollectionMethodIdentifiers.courier &&
    product.price_delivery
      ? product.price_delivery.amount
      : product.price.amount
  return productPrice
}

export const returnProductCurrency = (
  product: Product | Addition | BaseRecommendedProduct,
  collectionMethod: CollectionMethod
) => {
  const productCurrency =
    collectionMethod === CollectionMethodIdentifiers.courier &&
    product.price_delivery
      ? product.price_delivery.currency
      : product.price.currency
  return productCurrency
}

export const calculateTotalCartProductsPrice = (order: Order) => {
  let productsTotal = order.lines.reduce((acc: number, next: CartProduct) => {
    if (!next.buffer_delivery) {
      acc += next.subtotal_price.amount
    }
    return acc
  }, 0)
  return productsTotal
}

export const getCartTotal = (
  items: Product[],
  collectionMethod: CollectionMethod
) => {
  const sum = items?.reduce((acc: number, next: Product) => {
    acc += calculateTotalProductPrice(
      next,
      collectionMethod,
      next.addition_options!,
      next.quantity
    )
    return acc
  }, 0)

  return sum
}

export const triggerPulseAnimation = (ref: React.RefObject<HTMLElement>) => {
  if (ref.current) {
    ref.current.classList.add("animate-field")
    setTimeout(() => {
      ref.current?.classList.remove("animate-field")
    }, 300) // Match the duration of your animation
  }
}

export const triggerPulseAnimationFromId = (id: string) => {
  const element = document.getElementById(id)
  if (element) {
    element.classList.add("animate-field")
    setTimeout(() => {
      element.classList.remove("animate-field")
    }, 300) // Match the duration of your animation
  }
}
